import * as React from 'react'

import { IconCheck } from '@tabler/icons-react'
import { Command as CommandPrimitive } from 'cmdk'

import { cn } from '@/lib/utils'

/**
 * CommandItem wrapper that fixes cmdk's data-selected attribute handling.
 *
 * cmdk sets `data-selected={false}` on non-selected items, which React renders
 * as `data-selected="false"` in the DOM. The base ui component uses `data-selected:`
 * (Tailwind's `[data-selected]` selector) which matches ANY value — including "false".
 * This causes ALL items to get the selected background color.
 *
 * This wrapper uses `data-[selected=true]:` instead, which only matches when
 * the item is actually selected.
 *
 * Also removes `[[data-slot=dialog-content]_&]:rounded-lg!` which forces
 * pill-shaped items inside dialogs.
 */
function CommandItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[selected=true]:**:[svg]:text-accent-foreground group/command-item relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <IconCheck className="ml-auto opacity-0 group-has-[[data-slot=command-shortcut]]/command-item:hidden group-data-[checked=true]/command-item:opacity-100" />
    </CommandPrimitive.Item>
  )
}

/**
 * CommandShortcut wrapper that fixes the group-data-selected selector
 * to match only truly selected items (data-selected="true").
 */
function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        'text-muted-foreground group-data-[selected=true]/command-item:text-foreground ml-auto text-xs tracking-widest',
        className,
      )}
      {...props}
    />
  )
}

export { CommandItem, CommandShortcut }
